// src/features/bpch/shampoo/questions/ShampooQuestions.jsx
import React, { useState } from "react";
import QuestionsSection from "../../../../shared/sections/QuestionsSection";
import {
  ProductNameQuestion,
  MarketPositioningQuestion,
  SKUWeightQuestion,
  AnnualVolumeQuestion,
} from "../../../../shared/modules/questions";

import ManufacturingCountryQuestion from "../../../fb/cookies/questions/CookiesManufacturingCountryQuestion";

// Commodity-specific placeholder text mapping
const COMMODITY_PLACEHOLDERS = {
  Shampoos: "e.g., Anti-Dandruff Shampoo / Moisturizing Shampoo / Herbal Shampoo",
  "Face Serum": "e.g., Anti-Aging Serum / Vitamin C Serum / Hydrating Serum",
  Perfume: "e.g., Floral Eau de Parfum / Woody Cologne / Fresh Citrus Perfume",
  "Body Lotion": "e.g., Moisturizing Body Lotion / Cocoa Butter Lotion / Aloe Vera Lotion",
  "Bar Soap": "e.g., Glycerin Soap / Charcoal Detox Soap / Moisturizing Bar Soap",
};

/**
 * ShampooQuestions Component
 *
 * Encapsulates all product-related questions for BPCH commodities
 * Manages form data, validation, and question flow
 */
export default function ShampooQuestions({
  onComplete,
  onDataChange,
  initialData,
  commodity = "Shampoo",
}) {
  const [productFormData, setProductFormData] = useState(
    initialData || {
      productName: "",
      marketPositioning: "",
      skuWeight: "",
      skuWeightUnit: "ml",
      annualVolume: "",
      manufacturingCountry: "",
    }
  );

  // Calculate initial step based on answered questions
  const getInitialStep = () => {
    if (!initialData) return 0;

    if (initialData.manufacturingCountry) return 4; // Last question
    if (initialData.annualVolume) return 3;
    if (initialData.skuWeight) return 2;
    if (initialData.marketPositioning) return 1;
    if (initialData.productName) return 0;
    return 0;
  };

  const [productChatStep, setProductChatStep] = useState(getInitialStep());

  // Check if all questions are answered
  const isAllAnswered =
    productFormData.productName?.trim() &&
    productFormData.marketPositioning &&
    productFormData.skuWeight &&
    parseFloat(productFormData.skuWeight) > 0 &&
    productFormData.annualVolume &&
    productFormData.manufacturingCountry;

  // Notify parent when data changes
  React.useEffect(() => {
    if (isAllAnswered) {
      onDataChange?.(productFormData);
    }
  }, [productFormData, isAllAnswered, onDataChange]);

  // Selected values for display
  const productSelectedValues = [
    { label: "Product Name:", value: productFormData.productName },
    { label: "Market Positioning:", value: productFormData.marketPositioning },
    {
      label: "SKU Volume:",
      value: productFormData.skuWeight
        ? `${productFormData.skuWeight} ${productFormData.skuWeightUnit}`
        : "",
    },
    { label: "Annual Volume:", value: productFormData.annualVolume },
    { label: "Manufacturing Country:", value: productFormData.manufacturingCountry },
  ];

  // Validation function
  const isNextDisabled = (step) => {
    switch (step) {
      case 0:
        return !productFormData.productName || productFormData.productName.trim() === "";
      case 1:
        return !productFormData.marketPositioning;
      case 2:
        return !productFormData.skuWeight || parseFloat(productFormData.skuWeight) <= 0;
      case 3:
        return !productFormData.annualVolume;
      case 4:
        return !productFormData.manufacturingCountry;
      default:
        return false;
    }
  };

  const handleComplete = () => {
    onComplete?.(productFormData);
  };

  return (
    <QuestionsSection
      selectedValues={productSelectedValues}
      currentStep={productChatStep}
      onStepChange={setProductChatStep}
      onComplete={handleComplete}
      brandColor="#ff6034"
      isNextDisabled={isNextDisabled}
      autoAdvanceOnSelect={true}
      autoAdvanceOnBlur={true}
    >
      <ProductNameQuestion
        value={productFormData.productName}
        onChange={(value) => setProductFormData({ ...productFormData, productName: value })}
        labelText={`Enter ${commodity} Product Name:`}
        placeHolderText={COMMODITY_PLACEHOLDERS[commodity] || `e.g., Premium ${commodity}`}
      />
      <MarketPositioningQuestion
        value={productFormData.marketPositioning}
        onChange={(value) => {
          setProductFormData({ ...productFormData, marketPositioning: value });
        }}
      />
      <SKUWeightQuestion
        value={productFormData.skuWeight}
        onChange={(value) => setProductFormData({ ...productFormData, skuWeight: value })}
        unit={productFormData.skuWeightUnit}
        unitOptions={["ml", "L"]}
        onUnitChange={(value) => setProductFormData({ ...productFormData, skuWeightUnit: value })}
        labelText="Enter SKU Volume:"
      />
      <AnnualVolumeQuestion
        value={productFormData.annualVolume}
        onChange={(value) => setProductFormData({ ...productFormData, annualVolume: value })}
      />
      <ManufacturingCountryQuestion
        value={productFormData.manufacturingCountry}
        onChange={(value) =>
          setProductFormData({ ...productFormData, manufacturingCountry: value })
        }
      />
    </QuestionsSection>
  );
}
